import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../../lib/api'
import { useAuthStore } from '../../../../store/authStore'
import { useAccountLimits } from '../../../credits/useAccountLimits'

export interface AccountWithOwnership {
  id: number
  nazwa_konta: string
  access_token: string
  refresh_token: string
  token_expires_at: string
  refresh_token_expires_at?: string
  needs_reauth?: boolean
  marketplace_type?: string
  marketplace_specific_data?: any
  created_at: string
  updated_at: string
  is_owner: boolean
}

const ACCOUNTS_QUERY_KEY = ['accounts'] as const

export function useSharedAccounts() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const { data: limitsData } = useAccountLimits()

  const {
    data,
    isLoading,
    isFetched,
    error,
    refetch: fetchSharedAccounts,
  } = useQuery<AccountWithOwnership[]>({
    queryKey: [...ACCOUNTS_QUERY_KEY, user?.id],
    queryFn: async () => {
      const response = await api.get('/accounts/')
      return response.data
    },
    enabled: !!user,
    staleTime: 30000,
    gcTime: 300000,
  })

  const accounts: AccountWithOwnership[] = data ?? []
  // No user yet → treat as ready (empty). Avoids GettingStarted↔Rich flash on refresh.
  const accountsReady = !user || isFetched

  const refreshTokenMutation = useMutation({
    mutationFn: async (accountId: number) => {
      const response = await api.post(`/allegro/refresh-token/${accountId}`)
      return response.data
    },
    onSuccess: (updatedAccount) => {
      queryClient.setQueryData<AccountWithOwnership[]>(
        [...ACCOUNTS_QUERY_KEY, user?.id],
        (oldData) => {
          if (!oldData) return oldData
          return oldData.map((acc) => (acc.id === updatedAccount.id ? updatedAccount : acc))
        },
      )
    },
  })

  const deleteAccountMutation = useMutation({
    mutationFn: async (accountId: number) => {
      await api.delete(`/accounts/${accountId}`)
      return accountId
    },
    onSuccess: (deletedAccountId) => {
      queryClient.setQueryData<AccountWithOwnership[]>(
        [...ACCOUNTS_QUERY_KEY, user?.id],
        (oldData) => {
          if (!oldData) return oldData
          return oldData.filter((acc) => acc.id !== deletedAccountId)
        },
      )
      // Invalidate limits cache so the banner updates immediately after deletion
      queryClient.invalidateQueries({ queryKey: ['accountLimits'] })
    },
  })

  const refreshAccountToken = async (accountId: number): Promise<boolean> => {
    try {
      await refreshTokenMutation.mutateAsync(accountId)
      return true
    } catch (err: any) {
      console.error('Failed to refresh token:', err)
      return false
    }
  }

  const deleteAccount = async (accountId: number): Promise<boolean> => {
    try {
      await deleteAccountMutation.mutateAsync(accountId)
      return true
    } catch (err: any) {
      console.error('Failed to delete account:', err)
      return false
    }
  }

  const getExpiringAccounts = (daysThreshold = 7): AccountWithOwnership[] => {
    const threshold = new Date()
    threshold.setDate(threshold.getDate() + daysThreshold)
    return accounts.filter((account) => {
      const expiresAt = new Date(account.token_expires_at)
      return expiresAt <= threshold
    })
  }

  const getOwnedAccounts = (): AccountWithOwnership[] => {
    return accounts.filter((account) => account.is_owner)
  }

  const canManageAccount = (account: AccountWithOwnership): boolean => {
    return account.is_owner
  }

  const ownedCount = limitsData?.owned ?? getOwnedAccounts().length
  const isUnlimited = limitsData?.is_unlimited ?? false
  // null max = unlimited; don't coalesce to a fake cap (breaks admin unlimited).
  const maxAccounts = isUnlimited ? null : (limitsData?.max ?? 5)

  return {
    accounts,
    isLoading,
    accountsReady,
    error: error?.message || null,
    fetchSharedAccounts,
    refreshAccountToken,
    deleteAccount,
    getExpiringAccounts,
    getOwnedAccounts,
    canManageAccount,
    totalAccounts: accounts.length,
    ownedCount,
    expiringCount: getExpiringAccounts().length,
    maxAccounts,
    isUnlimited,
    canAddAccounts: isUnlimited || maxAccounts == null || ownedCount < maxAccounts,
    limitsData,
  }
}
